import { Injectable } from '@angular/core'
import { reactor } from '../reactor'
import { customerOperations } from '../stores/customer-store'
import { CustomersService } from '../services/customers-service'
import { SubscriptionsService } from '../services/subscriptions-service'
import { Customer, Subscription } from '../types'
import { errorToast, successToast } from '../toaster'
import { donePromise } from '../utils'
import { CustomersNavQueries } from 'src/modules/customers-nav/customers-nav.queries'

@Injectable({
  providedIn: 'root'
})
export class CustomerActions {
  customersService = new CustomersService();
  subscriptionsService = new SubscriptionsService();

  constructor(private customersNavQueries: CustomersNavQueries) {}

  // doesn't touch server
  modifyCustomer(customerId: number, updates: object): void {
    reactor.dispatch(customerOperations.updateCustomer, { customerId, updates })
  }

  // hits server
  updateCustomer(customerId: number, serverUpdates: object, clientUpdates: object = {}): Promise<any> {
    const p = this.customersService.updateCustomer(customerId, serverUpdates).then(() => {
      // Remove when agency signup no longer using customer actions
      this.customersNavQueries.refetchCustomers().subscribe()

      const updates = Object.assign(clientUpdates, serverUpdates)
      reactor.dispatch(customerOperations.updateCustomer, { customerId, updates })
    })
    return donePromise(p)
  }

  createCustomer(body: object): Promise<any> {
    const p = this.customersService.createCustomer(body)
    p.then(newCustomer => {
      // Remove when agency signup no longer using customer actions
      this.customersNavQueries.refetchCustomers().subscribe()

      reactor.dispatch(customerOperations.setCustomer, newCustomer)
      successToast('Business info saved!')
    })
    .catch(() => {
      errorToast('An error occurred while saving the business information, please try again')
    })
    return p
  }

  loadCustomers(): Promise<any> {
    const p = this.customersService.getCustomers().then((customers) => {
      reactor.dispatch(customerOperations.setCustomers, customers)
    })
    .catch(() => {
      errorToast('An error occurred while loading subscriptions, please reload this page')
    })
    return donePromise(p)
  }

  loadCustomersWithSubscriptions(): Promise<any> {
    const p = Promise.all<any>([this.customersService.getCustomers(), this.subscriptionsService.getSubscriptions()])
      .then((results: [Customer[], Subscription[]]) => {
        const customers = results[0]
        const subscriptions = results[1]
        customers.forEach(c => {
          c.subscription = subscriptions.find(s => s.customerId === c.id)
        })
        reactor.dispatch(customerOperations.setCustomers, customers)
      }).catch(() => {
        errorToast('An error occurred while loading your subscription, please reload this page')
      })
    return donePromise(p)
  }

  loadCustomer(customerId: number): Promise<any> {
    const p = this.customersService.getCustomer(customerId).then((customer) => {
      reactor.dispatch(customerOperations.setCustomer, customer)
    })
    .catch(() => {
      errorToast('An error occurred while loading business information, please reload this page')
    })
    return donePromise(p)
  }

  loadCustomerWithSubscription(customerId: number): Promise<any> {
    const p = Promise.all<any>([this.customersService.getCustomer(customerId), this.subscriptionsService.getSubscriptionForCustomer(customerId)])
      .then((results: [Customer, Subscription]) => {
        const c = results[0]
        c.subscription = results[1]
        reactor.dispatch(customerOperations.setCustomer, c)
      }).catch(() => {
        errorToast('An error occurred while loading your subscription, please reload this page')
      })
    return donePromise(p)
  }

  removeCustomer(customerId: number): Promise<any> {
    const p = this.customersService.removeCustomer(customerId)
    .then((customer) => {
      successToast("Client removed successfully.")
      reactor.dispatch(customerOperations.removeCustomer, customerId)
    })
    return donePromise(p)
  }

  requestKeywords(customerId: number): Promise<any> {
    const req = this.customersService.requestKeywords(customerId).then(() => {
      reactor.dispatch(customerOperations.updateCustomer, { customerId, updates: { canRequestKeywords: false } })
      successToast('Your preferred writers have been notified that you need new topic ideas!')
    })
    .catch(() => {
      errorToast('An error occurred notifying your preferred writers, please try again later.')
    })
    return donePromise(req)
  }
}
